import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { Button } from "components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "components/ui/card";
import { Skeleton } from "components/ui/skeleton";
import { Spinner } from "components/ui/spinner";
import { ReactQueryErrorWrapper } from "components/shared/react-query-error-wrapper";
import { cn } from "@/lib/utils";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import {
  fetchBatchConfig,
  fetchCredits,
  fetchSubscription,
} from "data/queries-common";
import { quoteBatchJob, startBatchJob } from "data/mutations-common";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import * as Routes from "routes";
import { resolveIdentityId } from "utils/identity";
import {
  BatchConfigProps,
  StepperControlProps,
} from "pages/geoscape-batch/types";
import {
  JobType,
  UseOverageCreditsCard,
} from "pages/shared/use-overage-credits-card";
import { formatTimestampBatchJob } from "utils/format-date";

interface StartBatchJobProps {}

export const StartBatchJob = (
  props: StartBatchJobProps & BatchConfigProps & StepperControlProps,
) => {
  const [identityState] = useIdentity();
  const identityId = resolveIdentityId(identityState);
  const history = useHistory();

  const subscriptionQuery = useQuery({
    queryKey: [QueryKeys.subscription, identityId],
    queryFn: () => fetchSubscription(),
  });

  const isExternallyManaged =
    subscriptionQuery.data?.activeSubscription?.plan.externally_managed ??
    false;

  const queryClient = useQueryClient();

  const [isEnoughCredits, setIsEnoughCredits] = useState(false);

  const [quotedBatchConfigAttempts, setQuotedBatchConfigAttempts] = useState(0);

  const quoteJobMutation = useMutation({
    mutationKey: [QueryKeys.batchesQuoteJob, identityId],
    mutationFn: () => quoteBatchJob(props.batchConfig.batchInfo.batchId),
  });

  const quotedBatchConfigQuery = useQuery({
    queryKey: [QueryKeys.batchesQuotedBatchConfig, identityId],
    queryFn: () => fetchBatchConfig(props.batchConfig.batchInfo.batchId),
    refetchInterval: (query) => {
      const refetchTime = 1000;
      if (query.state.data?.status != "Quoted") return refetchTime;

      return false;
    },
  });

  useEffect(() => {
    if (quotedBatchConfigQuery.isSuccess && quotedBatchConfigQuery.data) {
      setQuotedBatchConfigAttempts((prev) => {
        const newCount = prev + 1;
        if (newCount % 5 === 0) {
          // Please keep this console log statement - AH
          console.log(`Sent quote mutation again, attempt ${newCount}`);
          quoteJobMutation.mutate();
        }
        return newCount;
      });

      if (
        quotedBatchConfigQuery.data.status === "Quoted" &&
        props.batchConfig.status !== "Quoted"
      ) {
        props.setBatchConfig(quotedBatchConfigQuery.data);
      }
    }
  }, [quotedBatchConfigQuery.data?.status]);

  const creditsQuery = useQuery({
    queryKey: [QueryKeys.credits, identityId],
    queryFn: () => fetchCredits(),
  });

  const startJobMutation = useMutation({
    mutationKey: [QueryKeys.batchesStartJob, identityId],
    mutationFn: () =>
      startBatchJob(
        props.batchConfig.batchInfo.batchId,
        props.batchConfig.displayName,
        props.batchConfig.batchInfo.totalRows
          ? props.batchConfig.batchInfo.totalRows
          : 0,
        props.batchConfig.batchInfo.estimatedCreditCost
          ? props.batchConfig.batchInfo.estimatedCreditCost
          : 0,
      ),
    onSuccess: () => {
      history.push(Routes.verifyStudio);
    },
  });

  useEffect(() => {
    quoteJobMutation.mutate();
  }, []);

  const isLoading =
    quoteJobMutation.isPending ||
    quotedBatchConfigQuery.isPending ||
    creditsQuery.isPending ||
    quotedBatchConfigQuery.data?.status !== "Quoted";

  const isSuccess =
    quoteJobMutation.isSuccess &&
    quotedBatchConfigQuery.isSuccess &&
    creditsQuery.isSuccess &&
    quotedBatchConfigQuery.data?.status === "Quoted";

  var creditBalance = 0;
  var remainingBalance = 0;
  if (isSuccess && creditsQuery.data) {
    creditBalance = creditsQuery.data.totalCreditsBalance;

    if (isExternallyManaged) {
      creditBalance = Math.max(creditBalance, 0);
    }

    remainingBalance =
      creditBalance - quotedBatchConfigQuery.data.batchInfo.estimatedCreditCost;
  }

  var isCreditsRemaining = false;
  if (creditsQuery.isSuccess && creditsQuery.data) {
    isCreditsRemaining = remainingBalance > 0;
  }

  const overageCardProps = {
    type: JobType.BATCH,
    className: "col-span-2 flex flex-col justify-between",
    jobCreditCost: quotedBatchConfigQuery.data?.batchInfo.estimatedCreditCost,
    isLoading: isLoading,
    setIsEnoughCredits: setIsEnoughCredits,
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Confirm and Start</CardTitle>
        <CardDescription>
          Please review the details below before starting your batch job.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <ReactQueryErrorWrapper
          queries={[subscriptionQuery, creditsQuery, quotedBatchConfigQuery]}
          mutations={[quoteJobMutation, startJobMutation]}
        />

        {isLoading && <CustomSkeleton />}

        {isSuccess && (
          <>
            <div className="grid lg:grid-cols-3 gap-4">
              <Card className="col-span-1">
                <CardContent>
                  <h3 className="text-lg font-medium mb-4">
                    Batch Job Information
                  </h3>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell>Total Rows</TableCell>
                        <TableCell>
                          {quotedBatchConfigQuery.data.batchInfo.totalRows.toLocaleString()}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Estimated Time To Finish</TableCell>
                        <TableCell>
                          {formatTimestampBatchJob(
                            quotedBatchConfigQuery.data.batchInfo
                              .estimatedRunTime,
                          )}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Credit Balance</TableCell>
                        <TableCell>
                          {isExternallyManaged
                            ? Math.max(creditBalance, 0).toLocaleString()
                            : creditBalance.toLocaleString()}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Estimated Credit Cost</TableCell>
                        <TableCell>
                          {quotedBatchConfigQuery.data.batchInfo.estimatedCreditCost.toLocaleString()}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          className={cn(
                            remainingBalance < 0 && "text-destructive",
                          )}
                        >
                          Remaining Balance
                        </TableCell>
                        <TableCell
                          className={cn(
                            remainingBalance < 0 && "text-destructive",
                          )}
                        >
                          {remainingBalance.toLocaleString()}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              <UseOverageCreditsCard
                {...overageCardProps}
                className="lg:col-span-2"
              />
            </div>
          </>
        )}
      </CardContent>
      <CardFooter className="flex flex-row justify-end gap-2">
        <Button
          variant="outline"
          onClick={() => props.setActiveStep(Math.max(props.activeStep - 1, 0))}
        >
          Back
        </Button>
        <Button
          disabled={
            startJobMutation.isPending ||
            !isSuccess ||
            (!isEnoughCredits && !isCreditsRemaining)
          }
          onClick={() => startJobMutation.mutate()}
        >
          {startJobMutation.isPending && <Spinner />}
          Start
        </Button>
      </CardFooter>
    </Card>
  );
};

const CustomSkeleton = () => {
  return (
    <>
      <div className="grid grid-cols-3 gap-4">
        <Skeleton className="h-62 col-span-1" />
        <Skeleton className="h-62 col-span-2" />
      </div>
      <div className="flex flex-row justify-end gap-4">
        <Skeleton className="h-9 w-16" />
        <Skeleton className="h-9 w-20" />
      </div>
    </>
  );
};
